import type { ModerationQueueItem } from "@/types/admin";
import { getLabel, getEmoji } from "@/utils/taxonomy";
import { WHAT_HAPPENED } from "@/shared/taxonomy";

interface ModerationCardProps {
  queueItem: ModerationQueueItem;
  experience: { whatHappened: string[]; context: string | null; companyId: string | null; function: string | null; seniority: string | null } | null;
  onClick: () => void;
}

export function ModerationCard({ queueItem, experience, onClick }: ModerationCardProps) {
  const confidencePercent = Math.round(queueItem.confidence * 100);

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-surface-container-lowest border border-white/5 rounded-xl p-stack-md hover:border-primary/30 hover:bg-surface-container-low transition-colors"
    >
      <div className="flex items-start justify-between gap-stack-md">
        <div className="flex-1 min-w-0">
          {/* What happened pills */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {experience?.whatHappened.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-container-high rounded-full font-label-xs text-label-xs text-on-surface"
              >
                {getEmoji(WHAT_HAPPENED, id as string)} {getLabel(WHAT_HAPPENED, id as string)}
              </span>
            ))}
          </div>

          {/* Context preview */}
          {experience?.context && (
            <p className="font-body-md text-body-md text-on-surface line-clamp-2 mb-2">
              &ldquo;{experience.context}&rdquo;
            </p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-2 text-on-surface-variant">
            {experience?.companyId && (
              <span className="font-label-xs text-label-xs">{experience.companyId}</span>
            )}
            {experience?.function && (
              <span className="font-label-xs text-label-xs">· {experience.function}</span>
            )}
            {experience?.seniority && (
              <span className="font-label-xs text-label-xs">· {experience.seniority}</span>
            )}
            <span className="font-label-xs text-label-xs">
              · {new Date(queueItem.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* AI Decision badge */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className={`px-2 py-0.5 rounded-full font-label-xs text-label-xs ${
              queueItem.aiDecision === "ESCALATE"
                ? "bg-secondary-container text-on-secondary-container"
                : queueItem.aiDecision === "REJECT"
                  ? "bg-error-container text-on-error-container"
                  : "bg-primary-container text-on-primary-container"
            }`}
          >
            {queueItem.aiDecision}
          </span>
          <span className="font-label-xs text-label-xs text-on-surface-variant">
            {confidencePercent}% conf
          </span>
        </div>
      </div>

      {/* Reason */}
      {queueItem.reason && (
        <p className="font-label-xs text-label-xs text-on-surface-variant mt-2 truncate">
          Reason: {queueItem.reason}
        </p>
      )}
    </button>
  );
}
